import React, { useState, useEffect, useRef } from 'react';
import { feedbackAPI } from '../../services/api';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import Toast from '../../components/common/Toast';

const Feedback = () => {
  const [feedback, setFeedback] = useState([]);
  const [loading, setLoading] = useState(true);
  const [toast, setToast] = useState({ show: false, message: '', type: 'success' });
  const [pagination, setPagination] = useState({ page: 1, limit: 20, total: 0, totalPages: 0 });
  const [filters, setFilters] = useState({ status: '', type: '', search: '' });
  const [selectedFeedback, setSelectedFeedback] = useState(null);
  const [updatingId, setUpdatingId] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [dropdownState, setDropdownState] = useState({ open: false, id: null, position: { top: 0, right: 0 } });
  const dropdownRef = useRef(null);
  const buttonRefs = useRef({});

  const fetchFeedback = async () => {
    setLoading(true);
    try {
      const params = {
        page: pagination.page,
        limit: pagination.limit,
        status: filters.status,
        type: filters.type,
        search: filters.search,
      };
      const response = await feedbackAPI.getSubmissions(params);
      if (response.data.success) {
        setFeedback(response.data.data.feedback);
        setPagination(prev => ({
          ...prev,
          total: response.data.data.pagination.total,
          totalPages: response.data.data.pagination.totalPages,
        }));
      }
    } catch (error) {
      setToast({ show: true, message: 'Failed to fetch feedback', type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchFeedback();
  }, [pagination.page, filters.status, filters.type, filters.search]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownState.open) {
        const buttonEl = buttonRefs.current[dropdownState.id];
        if (buttonEl && !buttonEl.contains(event.target) && dropdownRef.current && !dropdownRef.current.contains(event.target)) {
          setDropdownState({ open: false, id: null, position: { top: 0, right: 0 } });
        }
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [dropdownState]);

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters(prev => ({ ...prev, [name]: value }));
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= pagination.totalPages) {
      setPagination(prev => ({ ...prev, page: newPage }));
    }
  };

  const handleStatusChange = async (id, newStatus, adminResponse = null) => {
    setUpdatingId(id);
    setDropdownState({ open: false, id: null, position: { top: 0, right: 0 } });
    try {
      const response = await feedbackAPI.updateSubmission(id, { status: newStatus, admin_response: adminResponse });
      if (response.data.success) {
        setFeedback(prev => 
          prev.map(f => f.id === id ? response.data.data : f)
        );
        setToast({ show: true, message: 'Status updated successfully', type: 'success' });
        if (selectedFeedback && selectedFeedback.id === id) {
          setSelectedFeedback(response.data.data);
        }
      }
    } catch (error) {
      setToast({ show: true, message: 'Failed to update status', type: 'error' });
    } finally {
      setUpdatingId(null);
    }
  };

  const handleViewDetails = (item) => {
    setSelectedFeedback(item);
    setShowDetailModal(true);
  };

  const handleReplySubmit = (e) => {
    e.preventDefault();
    if (selectedFeedback && selectedFeedback.admin_response?.trim()) {
      handleStatusChange(selectedFeedback.id, 'replied', selectedFeedback.admin_response);
    } else {
      setToast({ show: true, message: 'Please enter a response before replying', type: 'warning' });
    }
  };

  const handleCloseDetailModal = () => {
    setShowDetailModal(false);
    setSelectedFeedback(null);
  };

  const toggleDropdown = (id, e) => {
    e.preventDefault();
    e.stopPropagation();
    
    if (dropdownState.open && dropdownState.id === id) {
      setDropdownState({ open: false, id: null, position: { top: 0, right: 0 } });
      return;
    }

    const buttonRect = e.currentTarget.getBoundingClientRect();
    setDropdownState({
      open: true,
      id: id,
      position: {
        top: buttonRect.bottom + window.scrollY + 5,
        right: window.innerWidth - buttonRect.right + window.scrollX,
      }
    });
  };

  const getStatusColor = (status) => {
    const colors = {
      new: 'primary',
      read: 'info',
      replied: 'warning',
      closed: 'secondary',
    };
    return colors[status] || 'secondary';
  };

  const getTypeColor = (type) => {
    const colors = {
      bug: 'danger',
      feature: 'warning',
      general: 'info',
    };
    return colors[type] || 'secondary';
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    return new Date(dateStr).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  if (loading) {
    return <LoadingSpinner />;
  }

  return (
    <div>
      <Toast 
        show={toast.show} 
        message={toast.message} 
        type={toast.type} 
        onClose={() => setToast({ ...toast, show: false })} 
      />

      <div className="page-header mb-4">
        <div className="d-flex justify-content-between align-items-center flex-wrap gap-3">
          <div>
            <h2 className="page-title">Feedback</h2>
            <p className="page-subtitle">User feedback submitted through the app</p>
          </div>
          <div className="d-flex gap-2 flex-wrap">
            <select
              className="form-select form-select-sm"
              style={{ width: 'auto', minWidth: '150px' }}
              value={filters.status}
              onChange={handleFilterChange}
              name="status"
            >
              <option value="">All Status</option>
              <option value="new">New</option>
              <option value="read">Read</option>
              <option value="replied">Replied</option>
              <option value="closed">Closed</option>
            </select>
            <select
              className="form-select form-select-sm"
              style={{ width: 'auto', minWidth: '150px' }}
              value={filters.type}
              onChange={handleFilterChange}
              name="type"
            >
              <option value="">All Types</option>
              <option value="bug">Bug Report</option>
              <option value="feature">Feature Request</option>
              <option value="general">General</option>
            </select>
            <input
              type="text"
              className="form-control form-control-sm"
              placeholder="Search name, email, message..."
              style={{ width: '250px' }}
              value={filters.search}
              onChange={handleFilterChange}
              name="search"
            />
          </div>
        </div>
      </div>

      <div className="stat-card">
        {feedback.length === 0 ? (
          <div className="text-center py-5">
            <i className="bi bi-chat-square-text text-muted" style={{ fontSize: '3rem' }}></i>
            <h5 className="mt-3 text-muted">No feedback found</h5>
            <p className="text-muted">No feedback submissions match your filters</p>
          </div>
        ) : (
          <>
            <div className="table-responsive">
              <table className="table table-hover mb-0">
                <thead className="table-light">
                  <tr>
                    <th>ID</th>
                    <th>User</th>
                    <th>Type</th>
                    <th>Message</th>
                    <th>Rating</th>
                    <th>Status</th>
                    <th>Date</th>
                    <th className="text-end">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {feedback.map((item) => (
                    <tr key={item.id}>
                      <td>#{item.id}</td>
                      <td>
                        <div>
                          <strong>{item.user_name || 'Anonymous'}</strong>
                          <br />
                          <small className="text-muted">{item.user_email}</small>
                        </div>
                      </td>
                      <td>
                        <span className={`badge bg-${getTypeColor(item.type)} text-capitalize`}>
                          {item.type}
                        </span>
                      </td>
                      <td>
                        <span style={{ maxWidth: '250px', display: 'inline-block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {item.message}
                        </span>
                      </td>
                      <td>
                        {item.rating ? (
                          <span className="text-warning">
                            {'★'.repeat(item.rating)}{'☆'.repeat(5 - item.rating)}
                          </span>
                        ) : (
                          <span className="text-muted">—</span>
                        )}
                      </td>
                      <td>
                        <span className={`badge bg-${getStatusColor(item.status)} text-capitalize`}>
                          {item.status}
                        </span>
                      </td>
                      <td>{formatDate(item.created_at)}</td>
                      <td className="text-end">
                        <div className="d-flex gap-1 justify-content-end">
                          <button
                            className="btn btn-outline-primary btn-sm"
                            onClick={() => handleViewDetails(item)}
                            title="View details"
                            disabled={updatingId === item.id}
                          >
                            <i className="bi bi-eye"></i>
                          </button>
                          <button
                            className="btn btn-outline-secondary btn-sm dropdown-toggle"
                            type="button"
                            ref={el => buttonRefs.current[item.id] = el}
                            onClick={(e) => toggleDropdown(item.id, e)}
                            disabled={updatingId === item.id}
                          >
                            Status
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {pagination.totalPages > 1 && (
              <nav className="mt-3" aria-label="Feedback pagination">
                <ul className="pagination justify-content-center mb-0">
                  <li className={`page-item ${pagination.page === 1 ? 'disabled' : ''}`}>
                    <button
                      className="page-link"
                      onClick={() => handlePageChange(pagination.page - 1)}
                      disabled={pagination.page === 1}
                    >
                      Previous
                    </button>
                  </li>
                  {Array.from({ length: Math.min(pagination.totalPages, 5) }, (_, i) => {
                    let pageNum;
                    if (pagination.totalPages <= 5) {
                      pageNum = i + 1;
                    } else if (pagination.page <= 3) {
                      pageNum = i + 1;
                    } else if (pagination.page >= pagination.totalPages - 2) {
                      pageNum = pagination.totalPages - 4 + i;
                    } else {
                      pageNum = pagination.page - 2 + i;
                    }
                    return (
                      <li key={pageNum} className={`page-item ${pagination.page === pageNum ? 'active' : ''}`}>
                        <button className="page-link" onClick={() => handlePageChange(pageNum)}>
                          {pageNum}
                        </button>
                      </li>
                    );
                  })}
                  <li className={`page-item ${pagination.page === pagination.totalPages ? 'disabled' : ''}`}>
                    <button
                      className="page-link"
                      onClick={() => handlePageChange(pagination.page + 1)}
                      disabled={pagination.page === pagination.totalPages}
                    >
                      Next
                    </button>
                  </li>
                </ul>
                <p className="text-center text-muted small mt-2">
                  Showing {(pagination.page - 1) * pagination.limit + 1} to {Math.min(pagination.page * pagination.limit, pagination.total)} of {pagination.total} submissions
                </p>
              </nav>
            )}
          </>
        )}
      </div>

      {/* Floating Dropdown Menu */}
      {dropdownState.open && (
        <div
          ref={dropdownRef}
          className="dropdown-menu show"
          style={{
            position: 'fixed',
            top: dropdownState.position.top,
            right: dropdownState.position.right,
            zIndex: 99999,
            display: 'block',
            minWidth: '180px',
            padding: '0.5rem 0',
            fontSize: '0.875rem',
            color: '#212529',
            backgroundColor: '#fff',
            backgroundClip: 'padding-box',
            border: '1px solid rgba(0,0,0,.15)',
            borderRadius: '0.375rem',
            boxShadow: '0 0.5rem 1rem rgba(0,0,0,.175)',
            maxHeight: '300px',
            overflowY: 'auto'
          }}
        >
          <button
            className="dropdown-item"
            onClick={() => handleStatusChange(dropdownState.id, 'new')}
            disabled={updatingId === dropdownState.id}
            style={{
              display: 'block',
              width: '100%',
              padding: '0.4rem 1.5rem',
              clear: 'both',
              fontWeight: '400',
              color: '#212529',
              textAlign: 'left',
              textDecoration: 'none',
              whiteSpace: 'nowrap',
              backgroundColor: 'transparent',
              border: '0',
              cursor: 'pointer'
            }}
            onMouseEnter={(e) => {
              e.target.style.backgroundColor = '#f8f9fa';
            }}
            onMouseLeave={(e) => {
              e.target.style.backgroundColor = 'transparent';
            }}
          >
            Mark as New
          </button>
          <button
            className="dropdown-item"
            onClick={() => handleStatusChange(dropdownState.id, 'read')}
            disabled={updatingId === dropdownState.id}
            style={{
              display: 'block',
              width: '100%',
              padding: '0.4rem 1.5rem',
              clear: 'both',
              fontWeight: '400',
              color: '#212529',
              textAlign: 'left',
              textDecoration: 'none',
              whiteSpace: 'nowrap',
              backgroundColor: 'transparent',
              border: '0',
              cursor: 'pointer'
            }}
            onMouseEnter={(e) => {
              e.target.style.backgroundColor = '#f8f9fa';
            }}
            onMouseLeave={(e) => {
              e.target.style.backgroundColor = 'transparent';
            }}
          >
            Mark as Read
          </button>
          <button
            className="dropdown-item"
            onClick={() => handleStatusChange(dropdownState.id, 'replied')}
            disabled={updatingId === dropdownState.id}
            style={{
              display: 'block',
              width: '100%',
              padding: '0.4rem 1.5rem',
              clear: 'both',
              fontWeight: '400',
              color: '#212529',
              textAlign: 'left',
              textDecoration: 'none',
              whiteSpace: 'nowrap',
              backgroundColor: 'transparent',
              border: '0',
              cursor: 'pointer'
            }}
            onMouseEnter={(e) => {
              e.target.style.backgroundColor = '#f8f9fa';
            }}
            onMouseLeave={(e) => {
              e.target.style.backgroundColor = 'transparent';
            }}
          >
            Mark as Replied
          </button>
          <hr style={{ margin: '0.4rem 0' }} />
          <button
            className="dropdown-item text-danger"
            onClick={() => handleStatusChange(dropdownState.id, 'closed')}
            disabled={updatingId === dropdownState.id}
            style={{
              display: 'block',
              width: '100%',
              padding: '0.4rem 1.5rem',
              clear: 'both',
              fontWeight: '400',
              color: '#dc3545',
              textAlign: 'left',
              textDecoration: 'none',
              whiteSpace: 'nowrap',
              backgroundColor: 'transparent',
              border: '0',
              cursor: 'pointer'
            }}
            onMouseEnter={(e) => {
              e.target.style.backgroundColor = '#f8f9fa';
            }}
            onMouseLeave={(e) => {
              e.target.style.backgroundColor = 'transparent';
            }}
          >
            Close
          </button>
        </div>
      )}

      {/* Detail Modal */}
      {showDetailModal && selectedFeedback && (
        <div 
          className="modal fade show d-block" 
          tabIndex="-1" 
          role="dialog"
          style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 1050 }}
          onClick={(e) => {
            if (e.target === e.currentTarget) {
              handleCloseDetailModal();
            }
          }}
        >
          <div className="modal-dialog modal-lg modal-dialog-scrollable" role="document">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Feedback Details</h5>
                <button 
                  type="button" 
                  className="btn-close" 
                  onClick={handleCloseDetailModal}
                ></button>
              </div>
              <div className="modal-body">
                {/* Status and Type in one line */}
                <div className="row mb-3">
                  <div className="col-12">
                    <div className="d-flex gap-3 align-items-center">
                      <div>
                        <label className="form-label fw-bold mb-0">Status:</label>
                        <span className={`badge bg-${getStatusColor(selectedFeedback.status)} text-capitalize ms-2`}>
                          {selectedFeedback.status}
                        </span>
                      </div>
                      <div>
                        <label className="form-label fw-bold mb-0">Type:</label>
                        <span className={`badge bg-${getTypeColor(selectedFeedback.type)} text-capitalize ms-2`}>
                          {selectedFeedback.type}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="row mb-3">
                  <div className="col-md-6">
                    <label className="form-label fw-bold">Name</label>
                    <p>{selectedFeedback.user_name || 'Anonymous'}</p>
                  </div>
                  <div className="col-md-6">
                    <label className="form-label fw-bold">Email</label>
                    <p>
                      <a href={`mailto:${selectedFeedback.user_email}`}>{selectedFeedback.user_email}</a>
                    </p>
                  </div>
                </div>

                <div className="row mb-3">
                  <div className="col-12">
                    <label className="form-label fw-bold">Message</label>
                    <div className="p-3 bg-light rounded" style={{ whiteSpace: 'pre-wrap' }}>
                      {selectedFeedback.message}
                    </div>
                  </div>
                </div>

                <div className="row mb-3">
                  <div className="col-md-6">
                    <label className="form-label fw-bold">Rating</label>
                    <p>
                      {selectedFeedback.rating ? (
                        <span className="text-warning">
                          {'★'.repeat(selectedFeedback.rating)}{'☆'.repeat(5 - selectedFeedback.rating)}
                        </span>
                      ) : (
                        <span className="text-muted">Not rated</span>
                      )}
                    </p>
                  </div>
                  <div className="col-md-6">
                    <label className="form-label fw-bold">Submitted</label>
                    <p>{formatDate(selectedFeedback.created_at)}</p>
                  </div>
                </div>

                {selectedFeedback.admin_response && (
                  <div className="row mb-3">
                    <div className="col-12">
                      <label className="form-label fw-bold">Admin Response</label>
                      <div className="p-3 bg-light rounded">
                        {selectedFeedback.admin_response}
                      </div>
                    </div>
                  </div>
                )}

                {selectedFeedback.status !== 'closed' && (
                  <form onSubmit={handleReplySubmit}>
                    <div className="row mb-3">
                      <div className="col-12">
                        <label className="form-label fw-bold">Admin Response</label>
                        <textarea
                          className="form-control"
                          rows={3}
                          placeholder="Type your response here..."
                          value={selectedFeedback.admin_response || ''}
                          onChange={(e) => setSelectedFeedback({ ...selectedFeedback, admin_response: e.target.value })}
                        />
                      </div>
                    </div>
                    <div className="row">
                      <div className="col-12 text-end">
                        <button
                          type="submit"
                          className="btn btn-primary"
                          disabled={updatingId === selectedFeedback.id}
                        >
                          {updatingId === selectedFeedback.id ? (
                            <>
                              <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                              Sending...
                            </>
                          ) : (
                            'Send Response & Mark Replied'
                          )}
                        </button>
                      </div>
                    </div>
                  </form>
                )}
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={handleCloseDetailModal}>
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Feedback;